/**
 * SA 担当顧客一覧 (v3.2.23+ / SA UI ①)。
 *
 * URL: /sa/contacts
 *
 * 機能:
 *   - 自分が ownerSaUid である顧客のリスト表示
 *   - 取得区分バッジ「弊社紹介 / 開拓顧客」(2026-08-18)。報酬率の区分がひと目で分かる。
 *     isCompanyReferral は Deal 単位の属性なので、自分が SA の商談から集計して顧客行に出す。
 *   - 「新規顧客追加」ボタン → ダイアログで最小フィールド (氏名・メアド・etc.) 入力 → createContactBySa Callable
 *   - 各行の「商談を作成」リンクで /sa/deals?newContactId=... に飛んで起票
 */
import { SA_RETENTION_DEFAULT_REMINDER_DAYS, resolveRetentionState } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { ArrowRight, CalendarClock, Mail, Plus, UserRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useDeals } from '@/hooks/useDeals';
import { useSaContacts } from '@/hooks/useMailContacts';
import { useSalesSettingsWithDefaults } from '@/hooks/useSettings';
import { functions } from '@/lib/firebase';
import { toDate } from '@/lib/firestoreTime';
import { paths } from '@/routes/paths';

interface CreateContactPayload {
  name: string;
  email: string;
  companyName?: string;
  phone?: string;
  salesMemo?: string;
  kind?: 'btob' | 'btoc';
}

interface CreateContactResult {
  ok: true;
  contactId: string;
  created: boolean;
}

const createFn = httpsCallable<CreateContactPayload, CreateContactResult>(
  functions,
  'createContactBySa',
);

export function SaContactsPage() {
  const { user } = useAuth();
  const contactsQ = useSaContacts(user?.uid);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [salesMemo, setSalesMemo] = useState('');
  const [kind, setKind] = useState<'btob' | 'btoc'>('btoc');

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('顧客名は必須です');
      return;
    }
    if (!email.trim()) {
      toast.error('メールアドレスは必須です');
      return;
    }
    setSaving(true);
    try {
      const r = await createFn({
        name: name.trim(),
        email: email.trim(),
        companyName: companyName.trim() || undefined,
        phone: phone.trim() || undefined,
        salesMemo: salesMemo.trim() || undefined,
        kind,
      });
      if (r.data.created) {
        toast.success('顧客を追加しました');
      } else {
        toast.info('既存の顧客と統合しました (担当紐付け完了)');
      }
      setCreateOpen(false);
      setName('');
      setEmail('');
      setCompanyName('');
      setPhone('');
      setSalesMemo('');
      setKind('btoc');
      void contactsQ.refetch();
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const contacts = useMemo(() => contactsQ.data ?? [], [contactsQ.data]);

  // 取得区分 (弊社紹介 / 開拓顧客) は Deal.isCompanyReferral が情報源。顧客単位の属性ではないため、
  // 自分が SA (salesAgentUid) の商談だけを顧客ごとに集計する。SaDealsPage と同じ useDeals() を
  // 引数なしで呼ぶのでクエリキャッシュを共有する (追加の read は発生しない)。
  // 1 顧客に両区分の商談が混在しうるので、その場合は両方のバッジを出す。
  const dealsQ = useDeals();
  const acquisitionByContact = useMemo(() => {
    const uid = user?.uid;
    const map = new Map<string, { referral: boolean; selfDeveloped: boolean }>();
    if (!uid) return map;
    for (const d of dealsQ.data ?? []) {
      if (d.salesAgentUid !== uid || !d.contactId) continue;
      const cur = map.get(d.contactId) ?? {
        referral: false,
        selfDeveloped: false,
      };
      if (d.isCompanyReferral === true) cur.referral = true;
      else cur.selfDeveloped = true;
      map.set(d.contactId, cur);
    }
    return map;
  }, [dealsQ.data, user?.uid]);
  // 1 レンダー内で基準時刻を固定 (行ごとに new Date() すると残日数がぶれる)
  const now = useMemo(() => new Date(), []);
  // 外部SA は role='viewer' なので settings/sales を read できる (rules: owner/editor/viewer)
  const { merged: salesSettings } = useSalesSettingsWithDefaults();
  const reminderDays = salesSettings.saRetentionReminderDays ?? SA_RETENTION_DEFAULT_REMINDER_DAYS;

  return (
    <Screen
      title="自分の顧客"
      description="あなたが担当する顧客のリストです。各顧客には保有期限があり、期限までにあなたの商談でご入金があると期限が自動でリセットされます。期限を過ぎると担当から外れます。「弊社紹介 / 開拓顧客」バッジは報酬率の区分で、開拓顧客はベース報酬率にカテゴリー別の加算率が上乗せされます。"
      actions={
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          <Plus className="size-3.5" />
          新規顧客追加
        </Button>
      }
    >
      {contactsQ.isLoading ? (
        <Spinner />
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={UserRound}
          title="担当顧客がまだありません"
          description="「新規顧客追加」から登録するか、SA リファラルリンク経由でのフォーム送信で自動的に紐付きます。"
        />
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => {
            const until = toDate(c.saRetentionUntil);
            const { state, daysLeft } = resolveRetentionState({
              hasOwnerSa: true,
              until,
              now,
              reminderDays,
            });
            const acq = acquisitionByContact.get(c.id);
            // 商談がまだ無い顧客 (自分で追加した / リフェラル経由) は開拓顧客扱い。
            // 弊社紹介は必ず商談経由で付くため、商談ゼロで弊社紹介になることはない。
            const showSelfDeveloped = acq ? acq.selfDeveloped : true;
            return (
              <div
                key={c.id}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 flex items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">
                      {c.name || '(名前未設定)'}
                    </span>
                    {c.kind === 'btob' && (
                      <Badge variant="default" className="text-[10px]">
                        法人
                      </Badge>
                    )}
                    {c.promotionLevel === 'customer' && (
                      <Badge variant="approved" className="text-[10px]">
                        顧客化
                      </Badge>
                    )}
                    {acq?.referral && (
                      <Badge
                        variant="sent"
                        className="text-[10px]"
                        title="弊社紹介の商談があります。この商談の SA 報酬はベース報酬率のまま計算されます (開拓顧客加算なし)。"
                      >
                        弊社紹介
                      </Badge>
                    )}
                    {showSelfDeveloped && (
                      <Badge
                        variant="accent"
                        className="text-[10px]"
                        title={
                          acq
                            ? '開拓顧客の商談があります。SA 報酬はベース報酬率 + 開拓顧客加算率 (カテゴリー別、個別設定があればその率) で計算されます。'
                            : 'まだ商談がありません。起票すると既定で開拓顧客 (加算あり) として扱われます。'
                        }
                      >
                        開拓顧客
                      </Badge>
                    )}
                    {state === 'expiring' && (
                      <Badge variant="draft" className="text-[10px]">
                        期限間近 あと{daysLeft}日
                      </Badge>
                    )}
                    {state === 'expired' && (
                      <Badge variant="danger" className="text-[10px]">
                        期限切れ
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-2 mt-0.5">
                    <Mail className="size-3" />
                    <span className="truncate">{c.defaultEmail}</span>
                    {c.companyName && <span className="truncate">/ {c.companyName}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 justify-end">
                    <CalendarClock className="size-3" />
                    顧客期限
                  </div>
                  <div
                    className={`text-[11px] font-semibold tabular-nums ${
                      state === 'expired'
                        ? 'text-[var(--accent-red)]'
                        : state === 'expiring'
                          ? 'text-[var(--accent-amber)]'
                          : 'text-[var(--text-secondary)]'
                    }`}
                  >
                    {until ? until.toLocaleDateString('ja-JP') : '無期限'}
                  </div>
                </div>
                <Link
                  to={paths.saContactDetail.replace(':contactId', c.id)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-sm border border-[var(--border)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)] transition-colors"
                >
                  顧客詳細を開く
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>新規顧客を追加</DialogTitle>
            <DialogDescription>
              担当顧客として登録します。すでに同じメアドの顧客が存在する場合は、その顧客に紐付けます。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">氏名 *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例: 山田 太郎"
                />
              </div>
              <div>
                <Label className="text-xs">顧客タイプ *</Label>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value === 'btob' ? 'btob' : 'btoc')}
                  className="w-full px-2 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                >
                  <option value="btoc">個人 (BtoC)</option>
                  <option value="btob">法人 (BtoB)</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">メールアドレス *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@example.com"
              />
            </div>
            {kind === 'btob' && (
              <div>
                <Label className="text-xs">会社名 (任意)</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="例: 株式会社サンプル"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">電話番号 (任意)</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="例: 03-1234-5678"
              />
            </div>
            <div>
              <Label className="text-xs">営業メモ (任意)</Label>
              <Textarea
                value={salesMemo}
                onChange={(e) => setSalesMemo(e.target.value)}
                rows={2}
                placeholder="例: 決裁者は社長、導入は会計年度末を希望"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={saving}>
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={saving || !name.trim() || !email.trim()}
            >
              {saving ? '追加中…' : '追加'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}
